// Thread (job page round 2, Amir 26 Oct point 11: "Notes and messaging, a section of its own: add a
// note, text the customer, message, with the thread"): a record's conversation, oldest first with the
// composer under the newest, the way people read one; a pinned note sits on top. Notes for the office,
// texts to and from the customer, messages to and from the techs. Its rows are History's rows
// (HistoryItem: the time in the same column, the kind's glyph, who did what, the words quoted), so the
// section lines up with History and every row opens its detail the same way. How a message went is
// said in words ("delivered", "read"); one that didn't send says so in red. Past the latest ten, the
// earlier ones fold into one line that shows them in place. Reads only --thread-*; HistoryItem, Link
// read their own.
import type { ReactNode } from 'react';
import { HistoryItem } from '../HistoryItem/HistoryItem.tsx';
import { Link } from '../Link/Link.tsx';
import './Thread.css';

export type ThreadChannel = 'note' | 'text-out' | 'text-in' | 'techs-out' | 'techs-in' | 'email-out';

export interface ThreadMessage {
  id: string;
  /** Who wrote it: "Dana", "Jordan Harper". */
  who: string;
  /** To whom: "Jordan", "Sam and Lee"; none for a note or a message to the office. */
  to?: string;
  channel: ThreadChannel;
  /** "8:14 a.m." */
  at: string;
  /** The day it's under: "Today · Sun Oct 25"; a heading when it changes. */
  day: string;
  text: string;
  /** How it went, muted: "delivered", "read 8:20 a.m.". */
  status?: string;
  /** What went wrong, in red: "Didn’t send: the number can’t take texts". */
  problem?: string;
  /** Pinned on top of the thread (a note the office must see). */
  isPinned?: boolean;
}

/** Who did what, the head of a row: names in bold. */
export function threadHead(m: Pick<ThreadMessage, 'who' | 'to' | 'channel'>): ReactNode {
  const who = <b>{m.who}</b>;
  const to = m.to ? <b>{m.to}</b> : null;
  switch (m.channel) {
    case 'note':
      return <>{who} wrote a note · office only</>;
    case 'text-out':
      return (
        <>
          {who} texted {to}
        </>
      );
    case 'text-in':
      return <>{who} texted the office</>;
    case 'techs-out':
      return (
        <>
          {who} messaged {to}
        </>
      );
    case 'techs-in':
      return <>{who} messaged the office</>;
    case 'email-out':
      return (
        <>
          {who} emailed {to}
        </>
      );
  }
}

export interface ThreadProps {
  /** For screen readers: "Notes and messages on job 24612". */
  label: string;
  messages: ThreadMessage[];
  /** Each row's head opens its detail. */
  onOpen?: (id: string) => void;
  /** Under the newest message: the Composer. */
  composer?: ReactNode;
  /** Earlier messages folded away, and the line that shows them. */
  earlier?: { count: number; onShow: () => void };
  emptyText?: string;
  /** The day headings' level: one under the section's heading (3, under an h2 section). */
  dayLevel?: 3 | 4;
}

export function Thread({
  label,
  messages,
  onOpen,
  composer,
  earlier,
  emptyText = 'Nothing written yet.',
  dayLevel = 3,
}: ThreadProps) {
  const Day = dayLevel === 3 ? 'h3' : 'h4';
  const pinned = messages.filter((m) => m.isPinned);
  const rest = messages.filter((m) => !m.isPinned);
  const rows: ReactNode[] = [];
  let day = '';
  for (const m of rest) {
    if (m.day !== day) {
      rows.push(
        <Day key={`d-${m.day}`} className="fl-thread__day">
          {m.day}
        </Day>,
      );
      day = m.day;
    }
    rows.push(<Row key={m.id} m={m} onOpen={onOpen} />);
  }
  return (
    <div className="fl-thread" role="region" aria-label={label}>
      {pinned.map((m) => (
        <Row key={m.id} m={m} onOpen={onOpen} isPinned />
      ))}
      {earlier && earlier.count > 0 ? (
        <p className="fl-thread__earlier">
          <Link onPress={earlier.onShow}>
            Earlier · {earlier.count} more message{earlier.count > 1 ? 's' : ''}
          </Link>
        </p>
      ) : null}
      <div className="fl-thread__list">{rows}</div>
      {messages.length === 0 ? <p className="fl-thread__none">{emptyText}</p> : null}
      {composer ? <div className="fl-thread__compose">{composer}</div> : null}
    </div>
  );
}

function Row({
  m,
  onOpen,
  isPinned,
}: {
  m: ThreadMessage;
  onOpen?: (id: string) => void;
  isPinned?: boolean;
}) {
  return (
    <HistoryItem
      kind={m.channel === 'note' ? 'note' : 'text'}
      time={isPinned ? undefined : m.at}
      quote={m.text}
      detail={m.status}
      isPinned={isPinned}
      extra={m.problem ? <span className="fl-thread__bad">{m.problem}</span> : undefined}
      onOpen={onOpen ? () => onOpen(m.id) : undefined}
    >
      {threadHead(m)}
    </HistoryItem>
  );
}
